import { ref, computed, watch, onMounted } from 'vue'
import themeConfig from '@/config/theme.json'

// Shared state for theme management
const isDarkMode = ref(false)
const isInitialized = ref(false)

export function useTheme() {
  // Get current theme colors
  const currentTheme = computed(() => {
    return isDarkMode.value ? themeConfig.themes.dark : themeConfig.themes.light
  })

  // Apply theme to CSS variables
  const applyTheme = () => {
    const root = document.documentElement
    const theme = currentTheme.value

    // Primary colors
    root.style.setProperty('--theme-primary-color', theme.primary.color)
    root.style.setProperty('--theme-primary-hover', theme.primary.hover)
    root.style.setProperty('--theme-primary-text', theme.primary.text)
    root.style.setProperty('--theme-primary-headline', theme.primary.headline)

    // Secondary colors
    root.style.setProperty('--theme-secondary-color', theme.secondary.color)
    root.style.setProperty('--theme-secondary-hover', theme.secondary.hover)
    root.style.setProperty('--theme-secondary-text', theme.secondary.text)
    root.style.setProperty('--theme-secondary-headline', theme.secondary.headline)

    // Background colors
    root.style.setProperty('--theme-bg-primary', theme.background.primary)
    root.style.setProperty('--theme-bg-secondary', theme.background.secondary)
    root.style.setProperty('--theme-bg-tertiary', theme.background.tertiary)
    root.style.setProperty('--theme-bg-header', theme.background.header)

    // Border colors
    root.style.setProperty('--theme-border-default', theme.border.default)
    root.style.setProperty('--theme-border-hover', theme.border.hover)

    // Text colors
    root.style.setProperty('--theme-text-primary', theme.text.primary)
    root.style.setProperty('--theme-text-secondary', theme.text.secondary)
    root.style.setProperty('--theme-text-tertiary', theme.text.tertiary)
    root.style.setProperty('--theme-text-inverted', theme.text.inverted)

    // Button colors - Primary
    root.style.setProperty('--theme-button-primary-bg', theme.button.primary.bg)
    root.style.setProperty('--theme-button-primary-bg-hover', theme.button.primary.bgHover)
    root.style.setProperty('--theme-button-primary-text', theme.button.primary.text)
    root.style.setProperty('--theme-button-primary-border', theme.button.primary.border)

    // Button colors - Secondary
    root.style.setProperty('--theme-button-secondary-bg', theme.button.secondary.bg)
    root.style.setProperty('--theme-button-secondary-bg-hover', theme.button.secondary.bgHover)
    root.style.setProperty('--theme-button-secondary-text', theme.button.secondary.text)
    root.style.setProperty('--theme-button-secondary-border', theme.button.secondary.border)

    // Button colors - Ghost
    root.style.setProperty('--theme-button-ghost-bg', theme.button.ghost.bg)
    root.style.setProperty('--theme-button-ghost-bg-hover', theme.button.ghost.bgHover)
    root.style.setProperty('--theme-button-ghost-text', theme.button.ghost.text)
    root.style.setProperty('--theme-button-ghost-border', theme.button.ghost.border)

    // Form colors - Input
    root.style.setProperty('--theme-form-input-bg', theme.form.input.bg)
    root.style.setProperty('--theme-form-input-border', theme.form.input.border)
    root.style.setProperty('--theme-form-input-border-focus', theme.form.input.borderFocus)
    root.style.setProperty('--theme-form-input-text', theme.form.input.text)
    root.style.setProperty('--theme-form-input-placeholder', theme.form.input.placeholder)

    // Form colors - Label
    root.style.setProperty('--theme-form-label-text', theme.form.label.text)
    root.style.setProperty('--theme-form-label-required', theme.form.label.required)

    // Form colors - Error
    root.style.setProperty('--theme-form-error-text', theme.form.error.text)
    root.style.setProperty('--theme-form-error-bg', theme.form.error.bg)
    root.style.setProperty('--theme-form-error-border', theme.form.error.border)

    // Grid colors - Card
    root.style.setProperty('--theme-grid-card-bg', theme.grid.card.bg)
    root.style.setProperty('--theme-grid-card-bg-hover', theme.grid.card.bgHover)
    root.style.setProperty('--theme-grid-card-border', theme.grid.card.border)
    root.style.setProperty('--theme-grid-card-border-hover', theme.grid.card.borderHover)

    // Grid colors - Overlay
    root.style.setProperty('--theme-grid-overlay-bg', theme.grid.overlay.bg)
    root.style.setProperty('--theme-grid-overlay-bg-hover', theme.grid.overlay.bgHover)

    // Apply dark class to document element
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Initialize theme from storage or system preference
  const initializeTheme = () => {
    if (isInitialized.value) return

    // Check localStorage first
    const savedTheme = localStorage.getItem('theme')

    if (savedTheme) {
      isDarkMode.value = savedTheme === 'dark'
    } else {
      // Check system preference
      isDarkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches
    }

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      // Only update if user hasn't manually set a preference
      if (!localStorage.getItem('theme')) {
        isDarkMode.value = e.matches
      }
    })

    isInitialized.value = true
    applyTheme()
  }

  // Toggle dark mode
  const toggleDarkMode = () => {
    isDarkMode.value = !isDarkMode.value
    localStorage.setItem('theme', isDarkMode.value ? 'dark' : 'light')
  }

  // Set specific theme mode
  const setTheme = (mode) => {
    isDarkMode.value = mode === 'dark'
    localStorage.setItem('theme', mode)
  }

  // Watch for theme changes and apply
  watch([isDarkMode, currentTheme], () => {
    if (isInitialized.value) {
      applyTheme()
    }
  }, { deep: true })

  // Initialize on mount
  onMounted(() => {
    initializeTheme()
  })

  return {
    isDarkMode,
    currentTheme,
    toggleDarkMode,
    setTheme,
    applyTheme,
    initializeTheme,
  }
}
